import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { trpc, toastTrpcError } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import type { ProjectMemberItem } from "./timeline-types";

interface CommentComposerProps {
  canonicalId: string;
  members?: ProjectMemberItem[];
  onPosted: () => void;
}

export function CommentComposer({
  canonicalId,
  members = [],
  onPosted,
}: CommentComposerProps) {
  const [body, setBody] = useState("");
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [mentionMatch, setMentionMatch] = useState<{
    query: string;
    start: number;
    end: number;
  } | null>(null);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const add = trpc.workflow.comment.add.useMutation({
    onSuccess: () => {
      setBody("");
      setIsExpanded(false);
      setMentionMatch(null);
      onPosted();
    },
    onError: (err) => toastTrpcError(err, "Failed to add note"),
  });

  // Filter members matching the active mention query
  const filteredMembers = useMemo(() => {
    if (!mentionMatch) return [];
    const q = mentionMatch.query.toLowerCase().trim();
    if (!q) return members.slice(0, 6);
    return members
      .filter((m) => {
        if (!m.displayName) return false;
        const name = m.displayName.toLowerCase();
        return (
          name.includes(q) ||
          name.replace(/\s+/g, "").includes(q) ||
          name.replace(/[\s_-]+/g, "").includes(q)
        );
      })
      .slice(0, 6);
  }, [mentionMatch, members]);

  // Inspect textarea selection for `@mention` prefix
  const checkMention = useCallback((text: string, cursor: number) => {
    const textBefore = text.slice(0, cursor);
    const match = textBefore.match(/(?:^|\s)@([^\s@]*)$/);
    if (match && match.index !== undefined) {
      const matchIndex =
        match.index + (match[0].startsWith("@") ? 0 : match[0].indexOf("@"));
      setMentionMatch({
        query: match[1],
        start: matchIndex,
        end: cursor,
      });
      setSelectedIndex(0);
    } else {
      setMentionMatch(null);
    }
  }, []);

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    setBody(val);
    checkMention(val, e.target.selectionStart ?? val.length);
  };

  const handleSelectOrClick = () => {
    if (!textareaRef.current) return;
    const cursor = textareaRef.current.selectionStart ?? body.length;
    checkMention(body, cursor);
  };

  const insertMention = useCallback(
    (member: ProjectMemberItem) => {
      if (!mentionMatch || !member.displayName) return;
      const handle = member.displayName.includes(" ")
        ? member.displayName.replace(/\s+/g, "_")
        : member.displayName;

      const before = body.slice(0, mentionMatch.start);
      const after = body.slice(mentionMatch.end);
      const replacement = `@${handle} `;
      const nextBody = `${before}${replacement}${after}`;
      const nextCursor = before.length + replacement.length;

      setBody(nextBody);
      setMentionMatch(null);

      requestAnimationFrame(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
          textareaRef.current.setSelectionRange(nextCursor, nextCursor);
        }
      });
    },
    [body, mentionMatch],
  );

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    if (body.trim() && !add.isPending) {
      add.mutate({ canonicalId, body: body.trim() });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (mentionMatch && filteredMembers.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((prev) => (prev + 1) % filteredMembers.length);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex(
          (prev) =>
            (prev - 1 + filteredMembers.length) % filteredMembers.length,
        );
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        insertMention(filteredMembers[selectedIndex]);
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setMentionMatch(null);
        return;
      }
    }

    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      handleSubmit();
    }
  };

  // Close mention popup if user clicks outside
  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target as Node) &&
        textareaRef.current &&
        !textareaRef.current.contains(e.target as Node)
      ) {
        setMentionMatch(null);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  return (
    <form className="relative space-y-2" onSubmit={handleSubmit}>
      <Label htmlFor="comment-body" className="sr-only">
        Add a note
      </Label>
      <Textarea
        ref={textareaRef}
        id="comment-body"
        rows={isExpanded ? 3 : 1}
        value={body}
        onFocus={() => setIsExpanded(true)}
        onChange={handleTextChange}
        onKeyDown={handleKeyDown}
        onClick={handleSelectOrClick}
        onKeyUp={handleSelectOrClick}
        placeholder="Record a triage decision or investigation note... Type @ to mention a teammate"
        className="transition-[height] duration-150"
      />

      {/* Floating mention autocomplete menu */}
      {mentionMatch && filteredMembers.length > 0 && (
        <div
          ref={menuRef}
          role="listbox"
          aria-label="Teammate mentions"
          className="absolute z-30 mt-1 max-h-48 w-64 overflow-y-auto rounded-md border border-border bg-popover p-1 shadow-md animate-in fade-in-50 zoom-in-95"
        >
          <div className="px-2 py-1 text-2xs font-semibold text-muted-foreground border-b border-border/40">
            Mention teammate
          </div>
          {filteredMembers.map((member, idx) => {
            const isSelected = idx === selectedIndex;
            const initials = member.displayName
              ? member.displayName
                  .split(" ")
                  .map((w) => w[0])
                  .slice(0, 2)
                  .join("")
                  .toUpperCase()
              : "??";

            return (
              <button
                key={member.userId}
                type="button"
                role="option"
                aria-selected={isSelected}
                onClick={() => insertMention(member)}
                onMouseEnter={() => setSelectedIndex(idx)}
                className={cn(
                  "flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-xs transition-colors",
                  isSelected
                    ? "bg-foreground text-background font-medium shadow-xs"
                    : "text-foreground hover:bg-muted/60",
                )}
              >
                <span
                  className={cn(
                    "flex size-5 shrink-0 items-center justify-center rounded-full text-2xs font-semibold",
                    isSelected
                      ? "bg-background text-foreground"
                      : "bg-muted text-muted-foreground border border-border/60",
                  )}
                >
                  {initials}
                </span>
                <span className="truncate flex-1 min-w-0">
                  {member.displayName}
                </span>
                <span
                  className={cn(
                    "text-2xs capitalize shrink-0 opacity-70",
                    isSelected ? "text-background" : "text-muted-foreground",
                  )}
                >
                  {member.role}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {isExpanded && (
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>Press ⌘Enter to save · Type @ to mention</span>
          <div className="flex gap-2">
            {!body.trim() && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  setIsExpanded(false);
                  setMentionMatch(null);
                }}
              >
                Cancel
              </Button>
            )}
            <Button
              type="submit"
              size="sm"
              disabled={add.isPending || !body.trim()}
            >
              {add.isPending ? "Adding…" : "Add note"}
            </Button>
          </div>
        </div>
      )}
    </form>
  );
}
